import { OrderBaseView, OrderBaseViewData } from "../../order/base";
import { Template } from "../../../../../core/template/index";
import { FortumApp } from "../../../../fortumApp";
import TabControlView from "../../../../../core/views/tabControl/index";
import OrderItemCleansingTabView from "../tab/cleansing/index";
import OrderItemMeasuringView from "../tab/measuring/index";
import OrderItemOverviewView from "../tab/overview/index";
import OrderItemTabDetailView from "../tab/detail/index";
import { OrderItem } from "../../../../db/models/decontamination/orderItem";
import { emptyElement } from "../../../../../core/helpers/dom";
import { MeasuringTypeLike, MeasuringType } from "../../../../db/models/decontamination/index";
import { Measurement, MeasurementLike } from "../../../../db/models/decontamination/measurement";

export default class OrderItemDetailView extends OrderBaseView
{
    public template = Template.lazy('/app/views/decontamination/orderItem/detail/index.html');
    public app: FortumApp;
    public data: OrderItemDetailViewData;
    private wrapper: HTMLElement;
    private tabControl: TabControlView;
    private detailView: OrderItemTabDetailView;
    private cleansingView: OrderItemCleansingTabView;
    private measuringView: OrderItemMeasuringView;
    private overviewView: OrderItemOverviewView;

    private resolveActiveTabPage()
    {
        if (this.data.tab == null)
        {
            return 'detail';
        }

        return this.data.tab;
    }

    private validate()
    {
        this.data.measurementValidation = {
            noIncompleteMeasurements: true,
            noRequiredMeasuringTypesMissing: true,
        };

        let requiredMeasuringTypes = this.data.allMeasuringTypes.filter(o => o.optionality == MeasuringType.OPTIONALITY_REQUIRED);

        // Check for incomplete measurements
        if (this.data.item.measurements.find(o => Measurement.isIncomplete(o.value_before, o.value_after)))
        {
            this.data.measurementValidation.noIncompleteMeasurements = false;
        }

        // Check that all requirements are found at least one, and are filled or marked as incomplete
        for (let measuringType of requiredMeasuringTypes)
        {
            let measurement = this.data.item.measurements.find(o => 
            {
                // Type matches and the measurement is filled or is incomplete
                return o.type_id == measuringType.id && (Measurement.isValidStrict(o.value_before, o.value_after) || o.is_incomplete)
            });
            
            if (measurement == null)
            {
                this.data.measurementValidation.noRequiredMeasuringTypesMissing = false;
                break;
            }
        }

        let firstCleansingInstance = this.data.item.cleansing_instances[0];
        
        this.data.validation = {
            measurementsValid: this.data.measurementValidation.noIncompleteMeasurements && this.data.measurementValidation.noRequiredMeasuringTypesMissing,
            cleansingTypesValid: this.data.item.cleansing_instances.length > 0,
        }
        this.data.isValid = this.data.validation.cleansingTypesValid && this.data.validation.measurementsValid;
    }
    
    public beforeRender()
    {
        this.app.header.text =  '...';
        return super.beforeRender().then(() =>
        {
            return <PromiseLike<MeasuringTypeLike[]>>this.app.db.DeconMeasuringType.toArray();

        }).then((allMeasuringTypes) =>
        {
            this.data.isCompleted = this.data.item.state == OrderItem.STATE_COMPLETED;
            this.data.allMeasuringTypes = allMeasuringTypes;
            this.validate();

            this.app.header.text = doT.template('{{=it.text}}')({text: this.data.item.name});
            let renderPromises: PromiseLike<void>[] = [];

            this.tabControl = new TabControlView({
                activePage: this.resolveActiveTabPage(),
                tabPageClassNames: 'order-item',
                pages: [
                    {title: this.pgettext('decontamination.OrderItem.tab.title', 'Details'), id: 'detail'},
                    {title: this.pgettext('decontamination.OrderItem.tab.title', 'Cleansing'), id: 'cleansing'},
                    {title: this.pgettext('decontamination.OrderItem.tab.title', 'Measuring'), id: 'measuring'},
                    {title: this.pgettext('decontamination.OrderItem.tab.title', 'Overview'), id: 'overview'},
                ]
            });

            this.tabControl.on('activate:measuring', this.onMeasuringTabPageActivate);
            this.tabControl.on('activate:overview', this.onOnOverviewTabPageActivate);
            
            this.detailView = new OrderItemTabDetailView(this.data);
            this.cleansingView = new OrderItemCleansingTabView(this.data);
            this.measuringView = new OrderItemMeasuringView(this.data);
            this.overviewView = new OrderItemOverviewView(this.data);

            renderPromises.push(this.app.renderPartialView(this.tabControl));
            renderPromises.push(this.app.renderPartialView(this.detailView));
            renderPromises.push(this.app.renderPartialView(this.cleansingView));
            renderPromises.push(this.app.renderPartialView(this.measuringView));
            renderPromises.push(this.app.renderPartialView(this.overviewView));

            return <PromiseLike<void>>Promise.all(renderPromises).then(() => 
            {

            });
        });
    }

    private onMeasuringTabPageActivate = (sender, e: MouseEvent) =>
    {
        this.data.tab = 'measuring';
        this.app.refreshCurrentView(this.data);
    };

    private onOnOverviewTabPageActivate = (sender, e: MouseEvent) =>
    {
        this.data.tab = 'overview';
        this.app.refreshCurrentView(this.data);
    };

    public afterRender()
    {       
        this.wrapper.appendChild(this.tabControl.element);
        this.tabControl.getPage('detail').appendChild(this.detailView.element);
        this.tabControl.getPage('cleansing').appendChild(this.cleansingView.element);
        this.tabControl.getPage('measuring').appendChild(this.measuringView.element);
        this.tabControl.getPage('overview').appendChild(this.overviewView.element);

        return super.afterRender();
    }

    private onBackToOrderButtonClick()
    {
        this.app.router.go(`#/order/${this.data.order.uuid}/detail`, {tab: 'items'});
    }
}

export interface MeasurementValidation
{
    noIncompleteMeasurements?: boolean;
    noRequiredMeasuringTypesMissing?: boolean;
}

export interface OrderItemDetailViewData extends OrderBaseViewData
{
    isCompleted?: boolean;
    allMeasuringTypes?: MeasuringTypeLike[];
    tab?: string;
    isValid?: boolean;
    validation?: {measurementsValid: boolean; cleansingTypesValid: boolean;}
    measurementValidation?: MeasurementValidation;
}